import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Box, Button, TextField, Select, MenuItem, FormControl, InputLabel, InputAdornment, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SettingsExclusionView from "./SettingsExclusionView";
import { useSettingsExclusion } from "../../../hooks/useSettingsExclusion";
import useShowMessages from "../../../hooks/useShowMessages";
import useLoading from "../../../hooks/useLoading";
import { validateExcludedProduct } from '../../../services/settingsService';

//#region Constants
const EXCLUSION_TYPE_MAP = {
    PRODUCT: { label: "Partial - Product", propertyKey: "excludedProductIds" },
    FULL_SALES_CLASS: { label: "Full - Sales Class", propertyKey: "excludedTruckOrders" },
    PARTIAL_SALES_CLASS: { label: "Partial - Sales Class", propertyKey: "excludedGlaCodes" },
};
const CODE_TYPE_MAPPING = {
    PRODUCT: "product_id",
    FULL_SALES_CLASS: "truck_order",
    PARTIAL_SALES_CLASS: "sales_class",
};
const exclusionTypes = Object.entries(EXCLUSION_TYPE_MAP).map(([key, { label }]) => ({ value: key, label }));
//#endregion

//#region Helper Functions
const parseIngestExclusions = (value) => {
    const results = [];
    let uniqueId = 1;
    Object.entries(EXCLUSION_TYPE_MAP).forEach(([typeKey, { propertyKey }]) => {
        (value[propertyKey] || []).forEach((item) => {
            results.push({ id: uniqueId++, type: typeKey, ...item });
        });
    });
    return results;
};

const calculateIngestDiff = (initialItems, currentItems) => {
    const makeKey = (item) => `${item.type}|${item.code}`;
    const initialSet = new Set(initialItems.map(makeKey));
    const currentSet = new Set(currentItems.map(makeKey));
    const diff = [];

    initialItems.forEach((item) => {
        if (!currentSet.has(makeKey(item))) {
            diff.push({
                code_type: CODE_TYPE_MAPPING[item.type],
                code: item.code,
                isDeleted: true,
                propertyKey: EXCLUSION_TYPE_MAP[item.type].propertyKey
            });
        }
    });

    currentItems.forEach((item) => {
        if (!initialSet.has(makeKey(item))) {
            diff.push({
                code_type: CODE_TYPE_MAPPING[item.type],
                code: item.code,
                isDeleted: false,
                item: { code: item.code, vol_sku: item.vol_sku, cf_sku: item.cf_sku },
                propertyKey: EXCLUSION_TYPE_MAP[item.type].propertyKey
            });
        }
    });
    return diff;
};
//#endregion

const SettingsExclusionIngest = ({ onClose, openSuccessModal, updateSetting, settingSelected }) => {
    const { showMessage } = useShowMessages();
    const { setIsLoading } = useLoading(); // For local validation loading
    const [exclusionValue, setExclusionValue] = useState("");
    const [exclusionType, setExclusionType] = useState("PRODUCT");

    const settingsExlusion = useSettingsExclusion({
        settingSelected,
        parseInitialData: parseIngestExclusions,
        calculateDiff: calculateIngestDiff,
        updateSetting,
        onClose,
        openSuccessModal,
    });

    const handleAddExclusion = useCallback(async () => {
        const trimmedValue = exclusionValue.trim().toUpperCase();
        if (!trimmedValue) return;

        try {
            let newItemData = { code: trimmedValue, cf_sku: null, vol_sku: null };

            if (exclusionType === 'PRODUCT') {
                setIsLoading(true);
                const productRes = await validateExcludedProduct(trimmedValue);
                setIsLoading(false);
                if (!productRes || productRes.length === 0) {
                    return showMessage(`Product ID: ${trimmedValue} does not exist.`, "error");
                }
                newItemData = { ...newItemData, ...productRes };
            }

            settingsExlusion.setItems((prev) => [
                { id: Date.now(), type: exclusionType, ...newItemData },
                ...prev,               
            ]);
            setExclusionValue("");
            showMessage("Exclusion added successfully.", "success");
        } catch (error) {
            console.error("Error validating exclusion:", error);
            setIsLoading(false);
            showMessage("Error validating exclusion.", "error");
        }
    }, [exclusionValue, exclusionType, settingsExlusion.setItems, showMessage, setIsLoading]);

    const columns = useMemo(
        () => [
            {
                field: "type",
                headerName: "EXCLUSION TYPE",
                flex: 1,
                valueFormatter: (params) =>
                    EXCLUSION_TYPE_MAP[params.value]?.label || params.value,
            },
            {
                field: "code",
                headerName: "CODE",
                flex: 1,
            },
            {
                field: "cf_sku",
                headerName: "CF SKU",
                flex: 1,
            },
            {
                field: "vol_sku",
                headerName: "VOL PRODUCT",
                flex: 1.5,
            },
        ],
        []
    );

    const renderInputForm = () => (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="exclusion-type-label">Exclusion Type</InputLabel>
                <Select labelId="exclusion-type-label" value={exclusionType} label="Exclusion Type" onChange={(e) => setExclusionType(e.target.value)}>
                    {exclusionTypes.map((type) => 
                        (<MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)
                    )}
                </Select>
            </FormControl>
            <TextField
                label="Value"
                value={exclusionValue}
                onChange={(e) => setExclusionValue(e.target.value.toUpperCase())}
                sx={{ flex: 1 }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={() => setExclusionValue("")} edge="end"><ClearIcon /></IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <Button variant="contained" onClick={handleAddExclusion} className="button red small">Add</Button>
        </Box>
    );

    return (
        <SettingsExclusionView
            title={settingSelected?.label || "Ingest Exclusion"}
            columns={columns}
            renderInputForm={renderInputForm}
            onClose={onClose}
            {...settingsExlusion}
        />
    );
};

SettingsExclusionIngest.propTypes = {
    onClose: PropTypes.func.isRequired,
    openSuccessModal: PropTypes.func.isRequired,
    updateSetting: PropTypes.func.isRequired,
    settingSelected: PropTypes.object,
};

export default SettingsExclusionIngest;